"use client";

import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { Icon } from "@calcom/ui/components/icon";
import { useLocale } from "@calcom/lib/hooks/useLocale";

import {
  bookingPageUsernameFromPath,
  clearKnopkaBookingPageAppearance,
} from "../knopkaBookingPageAppearance";
import {
  clearKnopkaBookingPageAppearanceOnUnmount,
  useKnopkaBookingPageAppearance,
} from "../useKnopkaBookingPageAppearance";
import { KnopkaBookingPageBackground } from "./KnopkaBookingPageBackground";
import { KnopkaBookingPageAppearanceEditor } from "./KnopkaBookingPageAppearanceEditor";

function useCanEditBookingPage(pageUsername: string | null): boolean {
  const { data: session, status } = useSession();
  return useMemo(() => {
    if (!pageUsername || status !== "authenticated") return false;
    const user = session?.user as
      | { username?: string | null; orgAwareUsername?: string | null }
      | undefined;
    const candidates = [user?.username, user?.orgAwareUsername]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    return candidates.includes(pageUsername.toLowerCase());
  }, [pageUsername, session?.user, status]);
}

export function KnopkaBookingPageShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { t } = useLocale();
  const pageUsername = bookingPageUsernameFromPath(pathname);
  const isBookingSuccessPath = Boolean(pathname?.startsWith("/booking/"));
  const canEdit = useCanEditBookingPage(pageUsername);
  const { appearance, setAppearance } = useKnopkaBookingPageAppearance(pageUsername);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!pageUsername && !isBookingSuccessPath) {
      clearKnopkaBookingPageAppearance();
    }
  }, [pageUsername, isBookingSuccessPath]);

  useEffect(() => {
    return () => {
      if (pageUsername) {
        clearKnopkaBookingPageAppearance();
      }
    };
  }, [pageUsername]);

  clearKnopkaBookingPageAppearanceOnUnmount(pageUsername);

  return (
    <div className="knopka-booking-page-root relative min-h-full">
      <KnopkaBookingPageBackground active={appearance.active} />
      {children}
      {canEdit && pageUsername ? (
        <>
          {!editorOpen ? (
            <button
              type="button"
              className="bg-brand text-brand-contrast fixed bottom-5 right-5 z-[70] inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-lg transition hover:opacity-95"
              onClick={() => setEditorOpen(true)}>
              <Icon name="layers" className="h-4 w-4" aria-hidden />
              {t("knopka_page_appearance_edit")}
            </button>
          ) : null}
          {editorOpen ? (
            <KnopkaBookingPageAppearanceEditor
              username={pageUsername}
              onSaved={setAppearance}
              onClose={() => setEditorOpen(false)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
